import { useCallback, useState } from 'react';
import { supabase } from '../../../lib/supabase';

const normalizeCode = (code: string) => code.replace(/\s/g, '');

function isVerifiedFactor(status: string | undefined): boolean {
    return status === 'verified';
}

export function translateMfaError(err: unknown): string {
    const msg = err instanceof Error ? err.message : String(err ?? '');

    if (/friendly name.*already exists|factor.*already exists/i.test(msg)) {
        return 'Este autenticador já está cadastrado. Digite o código do app para concluir.';
    }
    if (/invalid totp|invalid code/i.test(msg)) {
        return 'Código inválido. Verifique o Google Authenticator.';
    }
    if (/rate limit|too many/i.test(msg)) {
        return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    }
    if (/mfa.*not enabled|totp.*not enabled/i.test(msg)) {
        return 'Autenticação TOTP não está ativa no projeto. Ative em Supabase → Authentication → MFA.';
    }
    if (/no.*factor|factor not found/i.test(msg)) {
        return 'Nenhum autenticador encontrado. Configure novamente.';
    }

    return msg || 'Erro na verificação em 2 etapas.';
}

export type EnrollmentResult =
    | { kind: 'new'; factorId: string; qrCode: string; secret: string }
    | { kind: 'already_active' };

let prepareEnrollmentPromise: Promise<EnrollmentResult> | null = null;

export function useMfa() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const prepareEnrollment = useCallback(async (): Promise<EnrollmentResult> => {
        if (prepareEnrollmentPromise) return prepareEnrollmentPromise;

        prepareEnrollmentPromise = (async (): Promise<EnrollmentResult> => {
            setLoading(true);
            setError(null);
            try {
                const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
                if (listError) throw listError;

                const totp = factors?.totp ?? [];
                if (totp.some((f) => isVerifiedFactor(f.status))) {
                    return { kind: 'already_active' };
                }

                for (const f of totp) {
                    if (!isVerifiedFactor(f.status)) {
                        await supabase.auth.mfa.unenroll({ factorId: f.id });
                    }
                }

                const { data, error: enrollError } = await supabase.auth.mfa.enroll({
                    factorType: 'totp',
                    issuer: 'Adlitics',
                    friendlyName: 'Adlitics MFA',
                });
                if (enrollError) throw enrollError;

                return {
                    kind: 'new',
                    factorId: data.id,
                    qrCode: data.totp.qr_code ?? '',
                    secret: data.totp.secret ?? '',
                };
            } catch (err: unknown) {
                const msg = translateMfaError(err);
                setError(msg);
                throw new Error(msg);
            } finally {
                setLoading(false);
                prepareEnrollmentPromise = null;
            }
        })();

        return prepareEnrollmentPromise;
    }, []);

    const verifyEnrollment = useCallback(async (factorId: string, code: string) => {
        setLoading(true);
        setError(null);
        try {
            const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
            if (cErr) throw cErr;

            const { error: vErr } = await supabase.auth.mfa.verify({
                factorId,
                challengeId: challenge.id,
                code: normalizeCode(code),
            });
            if (vErr) throw new Error('Código inválido. Verifique o app.');

            await supabase.auth.refreshSession();
        } catch (err: unknown) {
            const msg = translateMfaError(err);
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const challengeAndVerify = useCallback(async (code: string) => {
        setLoading(true);
        setError(null);
        try {
            const { data: factors, error: lErr } = await supabase.auth.mfa.listFactors();
            if (lErr) throw lErr;

            const factor = totpVerifiedOrFirst(factors?.totp ?? []);
            if (!factor) throw new Error('Nenhum fator MFA encontrado');

            const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
            if (cErr) throw cErr;

            const { error: vErr } = await supabase.auth.mfa.verify({
                factorId: factor.id,
                challengeId: challenge.id,
                code: normalizeCode(code),
            });
            if (vErr) throw new Error('Código inválido. Tente novamente.');

            await supabase.auth.refreshSession();
        } catch (err: unknown) {
            const msg = translateMfaError(err);
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, prepareEnrollment, verifyEnrollment, challengeAndVerify };
}

function totpVerifiedOrFirst(totp: { id: string; status: string }[]) {
    return totp.find((f) => isVerifiedFactor(f.status)) ?? totp[0];
}
